/** THE GOAL GAME, ON THE GIFT'S OWN SETTINGS.
 *
 *  One button to add this gift to the Goal Game, and — once it is in — the small
 *  "Goal Game" panel with that prize's own settings. The panel renders the SAME
 *  component as the Goal Game prize row and writes the SAME stored prize row
 *  (/goal-game/admin/prizes/…), so one source is edited from two places and
 *  there is never a second copy.
 *
 *  An organization without the Goal Game — or a signed-in user who may not
 *  touch it — simply never sees any of this: the library call is the gate.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Gamepad2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../ui/dialog';
import api from '../../lib/api';
import { PrizeGoalSettings } from '../goalgame/admin/PrizeGoalSettings';

export const useGoalGameLink = (orgId, enabled = true) => {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);

    const reload = useCallback(async () => {
        if (!enabled) { setData(null); return; }
        try {
            const res = await api.get('/goal-game/admin/gift-library', {
                // Every promotion: a gift added to any of them shows its settings.
                params: { organization_id: orgId || undefined, every_promotion: true },
            });
            setData(res.data);
        } catch (_e) { setData(null); }
    }, [orgId, enabled]);

    useEffect(() => { reload(); }, [reload]);

    const entry = (setupId, giftId) => {
        const design = (data?.designs || []).find((d) => d.setup_id === setupId);
        if (!design) return null;
        if (!giftId) return design;
        return (design.gifts || []).find((g) => g.gift_id === giftId) || null;
    };

    const inGame = (setupId, giftId) => !!entry(setupId, giftId)?.in_goal_game;
    /** Whether the SAVED setup already holds this gift. A gift that has only
     *  been typed on screen cannot be added yet — the server has never seen it. */
    const known = (setupId, giftId) => !!entry(setupId, giftId);
    const prizesFor = (setupId, giftId) => entry(setupId, giftId)?.prizes || [];

    const add = async (setupId, giftId, promotionId) => {
        setBusy(true);
        try {
            const res = await api.post('/goal-game/admin/prizes/from-gift', {
                organization_id: orgId || null,
                promotion_id: promotionId || null,
                setup_id: setupId,
                gift_id: giftId || null,
            });
            await reload();
            return res.data;
        } catch (_e) {
            return null;
        } finally { setBusy(false); }
    };

    const removePrize = async (prize) => {
        setBusy(true);
        try {
            await api.delete(`/goal-game/admin/prizes/${prize.id}`, {
                params: { organization_id: orgId || undefined,
                          promotion_id: prize.promotion_id || undefined },
            });
            await reload();
            return true;
        } catch (e) {
            toast.error(e.response?.data?.detail === 'last_active_prize'
                ? 'At least one prize must stay active'
                : 'Could not remove the prize');
            return false;
        } finally { setBusy(false); }
    };

    return { available: !!data, promotions: data?.promotions || [], orgId,
             inGame, known, prizesFor, add, removePrize, busy, reload };
};

export const AddToGoalGameButton = ({ link, setupId, giftId, testid }) => {
    const { t } = useTranslation();
    const [choosing, setChoosing] = useState(false);
    const prizes = link.prizesFor(setupId, giftId);

    const addTo = async (promotionId) => {
        setChoosing(false);
        const out = await link.add(setupId, giftId, promotionId);
        if (!out) { toast.error(t('goalGame.addToGoalGame')); return; }
        if (out.created) {
            toast.success(`${t('goalGame.addedToGoalGame')} — ${out.promotion_name || ''}`.trim());
        } else {
            toast.info(t('goalGame.alreadyInGoalGame'));
        }
    };

    const onClick = () => {
        const list = link.promotions || [];
        // One promotion, no question asked.
        if (list.length <= 1) addTo(list[0]?.promotion_id || null);
        else setChoosing(true);
    };

    const removeFromGame = async (prize) => {
        if (!window.confirm(t('goalGame.prizeRemoveFromGame'))) return;
        if (await link.removePrize(prize)) toast.success(t('goalGame.prizeRemovedFromGame'));
    };

    if (prizes.length) {
        return (
            <div className="w-full rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-3"
                 data-testid={`${testid}-panel`}>
                <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold flex items-center gap-1.5">
                        <Gamepad2 className="w-3.5 h-3.5" /> {t('goalGame.giftGoalPanel')}
                    </p>
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600"
                          data-testid={`${testid}-already`}>
                        <Check className="w-3 h-3" /> {t('goalGame.alreadyInGoalGame')}
                    </span>
                </div>
                <p className="text-[11px] text-slate-400">{t('goalGame.giftGoalPanelHint')}</p>
                {prizes.map((prize) => (
                    <div key={prize.id} className="space-y-2 border-t border-slate-100 dark:border-slate-800 pt-2">
                        <p className="text-[11px] text-slate-500" data-testid={`${testid}-promotion-${prize.id}`}>
                            {t('goalGame.prizeInPromotion', { name: prize.promotion_name })}
                        </p>
                        <PrizeGoalSettings prize={prize} orgId={link.orgId}
                                           promotionId={prize.promotion_id || null}
                                           onChanged={link.reload} />
                        <button type="button" disabled={link.busy}
                                onClick={() => removeFromGame(prize)}
                                data-testid={`${testid}-remove-${prize.id}`}
                                className="text-[11px] font-semibold text-slate-500 hover:text-red-500">
                            {t('goalGame.prizeRemoveFromGame')}
                        </button>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <>
            <Button type="button" variant="outline" size="sm" onClick={onClick}
                    disabled={link.busy} data-testid={testid}>
                {link.busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    : <Gamepad2 className="w-3 h-3 mr-1" />}
                {t('goalGame.addToGoalGame')}
            </Button>
            <Dialog open={choosing} onOpenChange={setChoosing}>
                <DialogContent data-testid={`${testid}-promotions`}>
                    <DialogHeader>
                        <DialogTitle>{t('goalGame.chooseGoalPromotion')}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2">
                        {(link.promotions || []).map((p) => (
                            <Button key={p.promotion_id || 'primary'} type="button" variant="outline"
                                    className="w-full justify-start"
                                    onClick={() => addTo(p.promotion_id || null)}
                                    data-testid={`${testid}-promotion-${p.promotion_id || 'primary'}`}>
                                {p.name}
                            </Button>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default AddToGoalGameButton;
